import React from 'react';
import debounce from 'lodash.debounce';
import { AssessmentTestContext } from '../../contexts/assessment-test';
import { INTRODUCTION_SECTION_ID } from '../../contexts/assessment-test/reducers/update-step-section';
import { getSectionClassName } from '../../contexts/assessment-test/helpers';
import { useActiveTestStep } from './use-active-step';
export const STEP_CONTAINER_CLASSNAME = 'assessment-test-step-container';

export function useTestSectionScrollState() {
  const [state, dispatch] = React.useContext(AssessmentTestContext);

  const { stepId, step, sectionId } = useActiveTestStep();
  const sections = React.useMemo(() => {
    return [
      ...(step.introduction
        ? [
            {
              id: INTRODUCTION_SECTION_ID,
              className: getSectionClassName(stepId, INTRODUCTION_SECTION_ID),
            },
          ]
        : []),
      ...step.sections.map((s) => ({
        id: s.id,
        className: getSectionClassName(stepId, s.id),
      })),
    ];
  }, [step.sections, step.introduction, stepId]);

  const scrollTo = React.useCallback(
    (sectionId: string) => {
      // Find the section by its class name pattern
      const section = sections.find((s) => s.id === sectionId);
      if (!section) {
        return;
      }

      const element = document.querySelector(`.${section.className}`);
      const container = document.querySelector(
        '[data-scroll-container="true"]'
      );

      if (!element || !container) {
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const scrollTop =
        elementRect.top + container.scrollTop - containerRect.top - 24; // margin offset

      container.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    },
    [sections]
  );

  const handleScroll = React.useCallback(() => {
    // Find the ContentWrapper which is the actual scrollable container
    const container = document.querySelector('[data-scroll-container="true"]');
    if (!container) {
      return;
    }

    // Check if we're scrolled to the bottom
    const isAtBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 1; // 1px tolerance

    // Find the active section from top to bottom
    // We iterate through sections in order and find the first one that is visible
    // This handles the case where a section is larger than the viewport
    let foundActiveSection: typeof sections[number] | null = null;

    for (const section of sections) {
      const element = document.querySelector('.' + section.className);
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Check if this section is visible within the container's viewport
      // A section is considered visible if:
      // 1. Its top is at or above the bottom of the container viewport, AND
      // 2. Its bottom is at or below the top of the container viewport
      // 3. It's within the container's horizontal bounds
      const isVisible =
        rect.top <= containerRect.bottom &&
        rect.bottom >= containerRect.top &&
        rect.left <= containerRect.right &&
        rect.right >= containerRect.left;

      if (isVisible && state.screen.type === 'STEP') {
        foundActiveSection = section;
        break; // Take the first visible section (top to bottom)
      }
    }

    // Edge case: if we're at the bottom, use the last section
    if (isAtBottom && sections.length > 0) {
      foundActiveSection = sections[sections.length - 1];
    }

    if (foundActiveSection && foundActiveSection.id !== sectionId) {
      dispatch({
        type: 'UPDATE_STEP_ACTIVE_SECTION',
        payload: {
          sectionId: foundActiveSection.id,
          timestamp: new Date(),
        },
      });
    }
  }, [sections, dispatch, sectionId, state.screen.type]);

  const debouncedScrollHandler = debounce(handleScroll, 350);

  React.useEffect(() => {
    const container = document.querySelector('[data-scroll-container="true"]');
    if (!container) {
      return;
    }

    container.addEventListener('scroll', debouncedScrollHandler);
    debouncedScrollHandler();
    return () => {
      container.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [debouncedScrollHandler]);

  return { scrollTo };
}
